import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowDown } from 'lucide-react';
import { useTenant } from '../../../context/TenantContext';
import { useMarketingCourses } from '../MarketingData';
import { parseEm } from '../inlineEm';
import { formatPrice } from '../../../lib/format';
import type { PublicCourse } from '../../../services/publicService';

const DEFAULTS = {
  headline: 'Engineering courses for people who already ship things.',
  subtitle:
    'Twelve focused programmes — distributed systems, ML infrastructure, security, performance. Taught by engineers currently working at the companies you would want references from. No bootcamp filler.',
  ctaText: 'Browse catalogue',
  ctaHref: '/courses',
};

export const UtilityHero: React.FC = () => {
  const { tenant } = useTenant();
  const hero = tenant?.branding.marketing?.hero ?? {};
  const headline = hero.headline || DEFAULTS.headline;
  const subtitle = hero.subtitle || DEFAULTS.subtitle;
  const ctaText = hero.ctaText || DEFAULTS.ctaText;
  const ctaHref = hero.ctaHref || DEFAULTS.ctaHref;
  const eyebrow = hero.eyebrow?.trim();

  const { courses } = useMarketingCourses();
  const feature: PublicCourse | null = courses[0] ?? null;

  return (
    <section id="hero" className="util-hero relative border-b border-[var(--util-rule)]">
      <div className="max-w-[1320px] mx-auto px-6">
        <div className="grid md:grid-cols-[1.4fr_1fr] min-h-[540px]">
          {/* Text */}
          <div className="util-hero-text py-16 md:py-20 md:pr-14 md:border-r md:border-[var(--util-rule)] flex flex-col justify-center">
            <div className="font-util-mono text-[11px] tracking-[0.04em] text-[var(--util-ink-soft)] mb-7 flex items-center gap-2">
              <span className="bg-[var(--util-accent-soft)] text-[var(--util-accent-ink)] px-2 py-0.5 rounded font-medium">v.4</span>
              <span>/ catalogue</span>
              <span className="text-[var(--util-ink-faint)]">/</span>
              <span>{eyebrow || 'spring ’26'}</span>
              <span className="text-[var(--util-ink-faint)]">/</span>
              <span>updated 04.26</span>
            </div>

            <h1 className="font-util-sans text-[40px] sm:text-5xl md:text-[64px] lg:text-[76px] leading-[1.0] tracking-[-0.035em] font-semibold text-[var(--util-ink)] mb-6 text-balance">
              <UtilHeading text={headline} />
            </h1>

            <p className="text-base md:text-lg leading-[1.5] text-[var(--util-ink-soft)] max-w-[56ch] mb-9">
              {subtitle}
            </p>

            <div className="flex items-center gap-3 flex-wrap">
              <Link
                to={ctaHref}
                className="util-btn-primary group inline-flex items-center gap-2 px-5 py-3 text-sm font-semibold rounded-md"
              >
                {ctaText}
                <kbd className="font-util-mono text-[11px] bg-white/20 px-1.5 py-0.5 rounded">↵</kbd>
              </Link>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 px-5 py-3 text-sm font-medium text-[var(--util-ink)] bg-white border border-[var(--util-rule)] rounded-md hover:bg-[var(--util-mute)] transition-colors"
              >
                <ArrowDown className="h-4 w-4" />
                View syllabus PDF
              </Link>
            </div>
          </div>

          {/* Spec sheet aside */}
          <aside className="util-spec relative bg-white p-6 flex flex-col">
            <span className="absolute top-0 left-0 right-0 h-1 bg-[var(--util-accent)]" />
            <div className="font-util-mono text-[10px] tracking-[0.06em] text-[var(--util-ink-faint)] uppercase mb-1">
              Featured · 01 / catalogue
            </div>
            <div className="font-util-mono text-[13px] font-medium text-[var(--util-ink)] mb-6 pb-3 border-b border-[var(--util-rule)] flex items-center justify-between">
              <span>{feature ? `${(feature.category || 'course').toUpperCase()} · ${feature.title.split(' ').slice(0, 4).join(' ')}` : 'Coming soon'}</span>
              {feature && (
                <span className="bg-[#2f7a3a] text-white px-2 py-0.5 text-[10px] tracking-[0.06em] rounded">ENROLLING</span>
              )}
            </div>

            {feature ? (
              <div className="flex flex-col gap-3.5 text-sm">
                {feature.level && <SpecRow k="Level" v={feature.level} />}
                {feature.category && <SpecRow k="Track" v={feature.category} />}
                {feature.instructorName && <SpecRow k="Instructor" v={feature.instructorName} />}
                {typeof feature.totalStudents === 'number' && feature.totalStudents > 0 && (
                  <SpecRow k="Enrolled" v={`${feature.totalStudents}`} mono />
                )}
                <SpecRow k="Format" v="Live + async" />
                <SpecRow k="Output" v="Project + reference" />
              </div>
            ) : (
              <div className="text-sm text-[var(--util-ink-soft)] py-8 text-center font-util-mono">
                Catalogue not yet populated.
              </div>
            )}

            {feature && (
              <div className="mt-auto pt-5 border-t border-[var(--util-rule)] flex justify-between items-end">
                <div className="font-util-sans font-semibold text-3xl tracking-[-0.025em] leading-none">
                  {feature.price > 0 ? formatPrice(feature.price) : 'Free'}
                  {feature.price > 0 && (
                    <small className="font-util-mono text-[11px] text-[var(--util-ink-faint)] font-normal ml-1">
                      incl. VAT
                    </small>
                  )}
                </div>
                <Link
                  to={`/course/${feature.id}`}
                  className="font-util-mono text-[12px] text-[var(--util-accent-ink)] border-b border-[var(--util-accent)] pb-0.5 hover:text-[var(--util-accent)] transition-colors inline-flex items-center gap-1"
                >
                  View full spec
                  <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            )}
          </aside>
        </div>
      </div>
    </section>
  );
};

const SpecRow: React.FC<{ k: string; v: string; mono?: boolean }> = ({ k, v, mono }) => (
  <div className="grid grid-cols-[110px_1fr] gap-4 items-baseline">
    <span className="font-util-mono text-[11px] uppercase tracking-[0.04em] text-[var(--util-ink-faint)]">{k}</span>
    <span className={`text-[var(--util-ink)] font-medium text-[13px] ${mono ? 'font-util-mono' : ''}`}>{v}</span>
  </div>
);

/** Utility headlines render `*word*` with an accent-colour underline behind. */
const UtilHeading: React.FC<{ text: string }> = ({ text }) => (
  <>{parseEm(text, (children, key) => (
    <span key={key} className="util-underline">{children}</span>
  ))}</>
);
